import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating positioning shell (no visible chrome of its own)
 * @property {HTMLButtonElement} addToCartButton - The floating button itself
 */

/**
 * A custom element that manages a floating sticky add-to-cart button.
 * Shows when the main buy buttons scroll out of view.
 *
 * It never runs its own add-to-cart logic: clicking it "puppet clicks" the
 * real button in the main product form (see #targetAddToCartButton below),
 * and #mirrorTargetButtonState keeps this button's visible state — text,
 * disabled-ness, and the Back in Stock app's "Sold out" -> "Notify me"
 * conversion (blocks/add-to-cart.liquid) — in sync with whatever the real
 * button is currently showing, rather than reimplementing any of that here.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {MutationObserver | null} */
  #targetButtonObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#targetButtonObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.boundingClientRect;
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#observeTargetButton();
  }

  /**
   * Keeps this button's visible state (text + disabled) in sync with the
   * real button in the main product form. Most states (available, sold
   * out, preorder, unavailable) already arrive correctly server-rendered
   * or via #handleVariantUpdate's morph below — the one thing neither of
   * those can know about is the Back in Stock app's client-side-only
   * "Sold out" -> "Notify me" conversion (blocks/add-to-cart.liquid),
   * which only ever touches the real button by id. This mirrors that
   * conversion across whenever it happens, without duplicating the app's
   * own logic.
   */
  #observeTargetButton() {
    this.#targetButtonObserver?.disconnect();
    if (!this.#targetAddToCartButton) return;

    this.#targetButtonObserver = new MutationObserver(this.#mirrorTargetButtonState);
    this.#targetButtonObserver.observe(this.#targetAddToCartButton, {
      attributes: true,
      attributeFilter: ['data-bis-notify'],
    });
    this.#mirrorTargetButtonState();
  }

  #mirrorTargetButtonState = () => {
    const target = this.#targetAddToCartButton;
    const { addToCartButton } = this.refs;
    if (!target || !addToCartButton) return;

    const isNotify = target.hasAttribute('data-bis-notify');
    if (!isNotify) return; // every other state already arrives correct via server render / morph

    const notifyText = target.querySelector('.add-to-cart-text__content')?.textContent?.trim();
    const stickyTextEl = addToCartButton.querySelector('.add-to-cart-text__content');
    if (notifyText && stickyTextEl) {
      stickyTextEl.textContent = notifyText;
    }

    addToCartButton.removeAttribute('disabled');
    addToCartButton.classList.add('button--sold-out-notify');
    /* Deliberately NOT setting data-bis-notify here. The Back in Stock
       app's own click listener is registered on document with capture:true
       and matches on that attribute — if this button carried it too, a
       real click here would get intercepted during the capture phase,
       before handleAddToCartClick's bubble-phase on:click handler ever
       runs, bypassing the puppet-click below entirely. Leaving it off
       means every click still goes through handleAddToCartClick, which
       puppet-clicks the real target button — and that real button DOES
       carry the attribute, so the app's listener still catches the
       resulting click correctly, just one hop later. */
  };

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar. It never adds
   * to cart itself — it puppet-clicks the real button in the main product
   * form (its click handler does the actual work: cart API call, cart
   * drawer, the Back in Stock notify flow when applicable) and just shows
   * its own brief "added" checkmark state in response. There's no product
   * image in this button anymore for a fly-to-cart animation to originate
   * from, so this is just the click delegation plus that checkmark.
   *
   * The checkmark is cleared by #handleCartAddComplete once the actual
   * cart request finishes, not on a fixed timer here — the real button's
   * own feedback animation runs roughly as long as a typical add-to-cart
   * request, so it visually covers the wait. A flat timeout on this button
   * would revert it well before slower requests finish, leaving it looking
   * idle for a second or more before the drawer suddenly opens.
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing, since it may
    // be a different element now, and re-point the mirror observer at it —
    // the notify conversion is client-side-only state that morph's fresh
    // server HTML has no way to know about, so if the new variant is also
    // sold out and already notify-converted, this re-checks that immediately
    // rather than waiting for its next attribute mutation.
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }
    this.#observeTargetButton();

    if (variant == null) {
      this.dataset.currentVariantId = '';
    }
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Handles cart add complete (success or error) - resets the puppet flag
   * and, once the real request is actually done, clears the checkmark
   * shortly after (mirroring the 800ms the real button holds its own
   * checkmark after its animation ends).
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
